import { Banner } from '@tackle-io/platform-ui';
import { AssignmentIcon } from 'packages/cosell/assets';
import FormSectionCard from '../components/FormSectionCard';
import { FormSectionConfig } from '../constants';
import useToggleState from 'hooks/useToggleState';
import { AddSolutionsFormDrawer } from './AddSolutionsForm/AddSolutionsForm';
import { FieldEnum, SolutionInputType } from './sectionSchema';
import { Box } from 'vendor/material';
import useSolutionsFieldArray from './useSolutionsFieldArray';
import SolutionListItem from './SolutionListItem/SolutionListItem';
import { useCallback, useEffect, useMemo } from 'react';
import { useReferralFormContext } from '../ReferralFormContext';

const NoSolutionsBanner = () => (
  <Banner
    borderPosition="top"
    isCollapsible
    title="At least one solution is required to create a referral."
    type="info"
    body={
      <>
        There are different types of solutions that can be added to a deal
        including solutions that are published by your company, Microsoft's
        solutions, or other third-party solutions or solution assessments.{' '}
        <b>
          Microsoft limits referrals to one IP-cosell eligible solution per
          referral.
        </b>
      </>
    }
  />
);

export const SolutionsFormSection = () => {
  const [drawerIsOpen, toggleDrawer] = useToggleState(false);
  const { fields, append, remove } = useSolutionsFieldArray();
  const { trigger } = useReferralFormContext();

  const handleAddSolutions = (solutions: SolutionInputType[]) => {
    append(solutions);
  };

  const selectedIds = useMemo(() => {
    return fields.map((field) => field?.id);
  }, [fields]);

  const removeSolution = useCallback(
    (index: number) => {
      remove(index);
    },
    [remove],
  );

  useEffect(() => {
    /** revalidate field when selectedIds changes,
     *  but only need to check that it is not empty
     */
    if (selectedIds.length > 1) return;
    trigger(FieldEnum.solutions);
  }, [selectedIds, trigger]);

  const hasNoSolutions = !fields.length;

  return (
    <>
      <FormSectionCard
        title={FormSectionConfig.solutions.title}
        subtitle={null}
        icon={<AssignmentIcon />}
        data-id={FormSectionConfig.solutions.dataId}
        sectionId={FormSectionConfig.solutions.id}
      >
        {hasNoSolutions ? (
          <>
            <NoSolutionsBanner />
            <FormSectionCard.BlockMessage>
              You haven't added any solutions yet.
            </FormSectionCard.BlockMessage>
          </>
        ) : null}

        <Box sx={{ marginBottom: '1rem' }}>
          {fields.map((field, index) => (
            <SolutionListItem
              key={field.id}
              fieldName={`${FieldEnum.solutions}.${index}`}
              solution={field}
              onDelete={() => removeSolution(index)}
            />
          ))}
        </Box>

        <FormSectionCard.AddButton onClick={toggleDrawer}>
          Add solution
        </FormSectionCard.AddButton>
      </FormSectionCard>

      <AddSolutionsFormDrawer
        onAddSolutions={handleAddSolutions}
        open={drawerIsOpen}
        onClose={toggleDrawer}
        selectedIds={selectedIds}
      />
    </>
  );
};

export default SolutionsFormSection;
